import {
  FALLBACK_CITIES,
  FALLBACK_COUNTRIES,
  FALLBACK_NEAREST_OFFICES,
  FALLBACK_STUDY_AREAS,
} from "../constants/onboardingFallbacks";
import type { WizardCountryOption, WizardOption, WizardOptions } from "../constants/onboardingFallbacks";
import { FRAPPE_URL, FRAPPE_API_KEY, FRAPPE_API_SECRET } from "./b2c/constants";
import { PROGRAM_LEVEL_OPTIONS_UNIFIED } from "./b2c/programLevelConstants";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL;
const RETRY_BACKOFF_MS = 500;

// One retry catches transient CMS/Frappe timeouts (the biggest cause of an empty
// wizard list). Anything beyond that is real downtime — let the static fallback
// in fetchWizardOptions take over.
async function fetchWithRetry(url: string, init?: RequestInit): Promise<Response | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(url, init);
      if (res.ok) return res;
    } catch (error) {
      if (attempt === 1) {
        console.error('[wizardOptionsApi] fetch failed after retry:', url, error);
      }
    }
    if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS));
  }
  return null;
}

function parseCfId(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function getRawCfId(raw: any): number | undefined {
  return (
    parseCfId(raw?.cfId) ??
    parseCfId(raw?.cf_id) ??
    parseCfId(raw?.courseFinderId) ??
    parseCfId(raw?.course_finder_id)
  );
}

function mapCountry(raw: any): WizardCountryOption {
  return {
    cfId: getRawCfId(raw),
    name: raw.name || "",
    imageUrl: raw.imageUrl || undefined,
    imageAltTag: raw.imageAltTag || undefined,
  };
}

function mapOption(raw: any): WizardOption {
  return {
    cfId: getRawCfId(raw),
    label: raw.label || raw.name || "",
    imageUrl: raw.imageUrl || undefined,
    imageAltTag: raw.imageAltTag || undefined,
  };
}


async function fetchCollection<T>(endpoint: string, mapper: (raw: any) => T): Promise<T[]> {
  const res = await fetchWithRetry(
    `${API_BASE}/${endpoint}?enabled=true&_sort=sortOrder:ASC`
  );
  if (!res) return [];
  try {
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      return data.map(mapper);
    }
  } catch (error) {
    console.error('[wizardOptionsApi] Failed to parse collection JSON:', endpoint, error);
  }
  return [];
}


async function fetchFrappeResource(path: string): Promise<WizardOption[]> {
  if (!FRAPPE_URL || !FRAPPE_API_KEY || !FRAPPE_API_SECRET) return [];
  const res = await fetchWithRetry(`${FRAPPE_URL}${path}`, {
    headers: { Authorization: `token ${FRAPPE_API_KEY}:${FRAPPE_API_SECRET}` },
  });
  if (!res) return [];
  try {
    const data = await res.json();
    return Array.isArray(data.data)
      ? data.data.map((item: any) => ({ label: item.name || '' }))
      : [];
  } catch (error) {
    console.error('[wizardOptionsApi] Failed to parse Frappe JSON:', path, error);
    return [];
  }
}

export async function fetchWizardOptions(): Promise<WizardOptions> {
  // pref-degree-levels result is discarded — degreeLevels uses hardcoded buckets.
  // Call is kept so the swap is reversible if product wants CMS options back.
  const [countries, , studyAreas, cities, nearestOffices, coachingOptions] =
    await Promise.all([
      fetchCollection("pref-countries-lists", mapCountry),
      fetchCollection("pref-degree-levels", mapOption),
      fetchCollection("pref-study-areas", mapOption),
      fetchFrappeResource('/api/method/get_cities_for_b2c'),
      fetchFrappeResource('/api/method/get_offices_for_b2c'),
      // Coaching courses from Frappe (KC Courses, sorted server-side), like cities/offices.
      fetchFrappeResource('/api/method/get_courses_for_b2c'),
    ]);

  // Substitute static fallbacks for any empty list — keeps the wizard usable
  // when CMS/Frappe is slow or down. Live data still wins when present.
  return {
    countries:      countries.length      ? countries      : FALLBACK_COUNTRIES,
    degreeLevels:   PROGRAM_LEVEL_OPTIONS_UNIFIED,
    studyAreas:     studyAreas.length     ? studyAreas     : FALLBACK_STUDY_AREAS,
    cities:         cities.length         ? cities         : FALLBACK_CITIES,
    nearestOffices: nearestOffices.length ? nearestOffices : FALLBACK_NEAREST_OFFICES,
    coachingOptions,
  };
}
